import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild, ChangeDetectionStrategy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { SimpleCodeValue } from '../../../../core/models/code-value.model';
import { LocationItem, NewLocationPayload, AssignedLocation } from '../../../../core/models/location.model';
import { UserManagementService } from '../../user-management.service';
import { ReplacePipe } from '../../../../utils/replace.pipe';
import { LookupItem } from '../../../../core/services/lookup.service';
import { ManageResidentsService } from '../../../../core/services/resident.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';

export interface FindLocationDialogData {
  areas: SimpleCodeValue[];
  currentAssignedLocations: AssignedLocation[];
  selectedResidentType: string;
  locationTypes: LookupItem[];
}

export interface FindLocationDialogResult {
  assignedLocations: AssignedLocation[];
}

@Component({
  selector: 'app-find-location-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatCheckboxModule,
    MatCardModule,
    MatToolbarModule,
    MatTabsModule,
    ReplacePipe,
    MatProgressSpinnerModule
  ],
  templateUrl: './find-location-dialog.component.html',
  styleUrls: ['./find-location-dialog.component.scss'],
})
export class FindLocationDialogComponent implements OnInit {
  private assignedPaginatorRef?: MatPaginator;
  private availablePaginatorRef?: MatPaginator;
  private destroy$ = new Subject<void>();

  @ViewChild('assignedPaginator') set assignedPaginator(paginator: MatPaginator | undefined) {
    this.assignedPaginatorRef = paginator;
    // Paginator is now available, set it on the data source
    if (this.assignedPaginatorRef) {
      this.assignedLocations.paginator = this.assignedPaginatorRef;
      // Re-update the data source to ensure paginator is correctly attached
      this.updateAssignedLocationsDataSource();
    }
  }

  @ViewChild('availablePaginator') set availablePaginator(paginator: MatPaginator | undefined) {
    this.availablePaginatorRef = paginator;
    // Paginator is now available, set it on the data source
    if (this.availablePaginatorRef) {
      this.locations.paginator = this.availablePaginatorRef;
    }
  }

  locationSearchForm: FormGroup;
  newLocationForm: FormGroup;
  locations = new MatTableDataSource<LocationItem>([]);
  assignedLocations = new MatTableDataSource<AssignedLocation>([]);

  // Use a Map for efficient lookups (O(1) complexity)
  private assignedLocationsMap = new Map<string, AssignedLocation>();
  locationsDetails: LocationItem[] = [];
  isLoading: boolean = false;
  activeTabIndex: number = 0;
  
  displayedLocationsColumns: string[] = ['areaName', 'locationName', 'qtrNumber', 'blockNumber', 'residentName', 'select'];
  displayedAssignedColumns: string[] = ['areaName', 'QuarterNo', 'blockNumber', 'locationName', 'locationAssignedTo', 'status', 'actions'];

  constructor(
    private dialogRef: MatDialogRef<FindLocationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FindLocationDialogData,
    private fb: FormBuilder,
    private userManagementService: UserManagementService,
    private manageResidentsService: ManageResidentsService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.locationSearchForm = this.fb.group({
      area: [''],
      searchText: ['']
    });

    this.newLocationForm = this.fb.group({
      newArea: ['', Validators.required],
      locationType: ['', Validators.required],
      qtrNumber: ['', Validators.required],
      blockNumber: ['', Validators.required],
      locationName: [''],
      locationCode: [''],
      phoneNumber: ['', [Validators.pattern(/^(?!.*(\d)\1{9})([1-9]\d{9})$/)]],
      locationDetails: ['']
    });

    this.newLocationForm.get('locationType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLocationCode());
    this.newLocationForm.get('qtrNumber')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLocationCode());
    this.newLocationForm.get('blockNumber')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLocationCode());
  }

  ngOnInit(): void {
    // Initialize assigned locations from the passed data
    (this.data.currentAssignedLocations || []).forEach((loc: AssignedLocation) => {
      // Create multiple keys to match how available locations may identify the same record
      // Backend/DTOs sometimes use `id`, `allocationId` or `locationId` depending on endpoint
      const possibleKeys: Array<string> = [];
      if (loc.id !== undefined && loc.id !== null) possibleKeys.push(String(loc.id));
      if ((loc as any).allocationId !== undefined && (loc as any).allocationId !== null) possibleKeys.push(String((loc as any).allocationId));
      if ((loc as any).locationId !== undefined && (loc as any).locationId !== null) possibleKeys.push(String((loc as any).locationId));
      // also include a fallback composite key used when no numeric id is present
      possibleKeys.push(`${loc.locationName}-${loc.quarterNumber}`);

      possibleKeys.forEach(k => this.assignedLocationsMap.set(k, loc));
    });
    console.log(this.data);
    
    this.updateAssignedLocationsDataSource();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateAssignedLocationsDataSource(): void {
    // The assignedLocationsMap may contain multiple keys that reference the same logical
    // AssignedLocation (we seed multiple keys like id/allocationId/locationId). To avoid
    // duplicate rows in the Assigned Locations table we deduplicate by a canonical id.
    const uniqueMap = new Map<string, AssignedLocation>();
    for (const loc of this.assignedLocationsMap.values()) {
      const canonicalId = (loc as any).allocationId ?? loc.id ?? (loc as any).locationId ?? `${loc.locationName}-${loc.quarterNumber}`;
      if (!uniqueMap.has(String(canonicalId))) {
        uniqueMap.set(String(canonicalId), loc);
      }
    }

    const nonDeletedLocations = Array.from(uniqueMap.values()).filter(loc => loc.status !== 'Deleted');
    this.assignedLocations.data = nonDeletedLocations;

    // Explicitly update the paginator's length
    if (this.assignedPaginatorRef) {
      this.assignedLocations.paginator = this.assignedPaginatorRef;
      this.assignedPaginatorRef.length = nonDeletedLocations.length;
    }

    // Debug: show the deduplicated assigned locations
    console.log('Assigned locations (deduplicated):', this.assignedLocations.data);
  }

  updateLocationCode() {
    const locationType = this.newLocationForm.get('locationType')?.value || '';
    const qtrNumber = this.newLocationForm.get('qtrNumber')?.value || '';
    const blockNumber = this.newLocationForm.get('blockNumber')?.value || '';

    if (locationType || qtrNumber || blockNumber) {
      this.newLocationForm.get('locationCode')?.setValue(
        `${locationType}${qtrNumber ? '-' + qtrNumber : ''}${blockNumber ? '/' + blockNumber : ''}`,
        { emitEvent: false }
      );
    }
  }

  onTabChange(event: any) {
    console.log(event);
    
    this.activeTabIndex = event.index;
  }

onSaveNewLocation(): void {
  if (this.newLocationForm.invalid) {
    this.newLocationForm.markAllAsTouched();
    this.cdr.detectChanges(); // Update UI to show errors
    return;
  }

  const formValue = this.newLocationForm.value;
  const payload: NewLocationPayload = {
    areaId: formValue.newArea,
    qtr: formValue.qtrNumber,
    block: formValue.blockNumber,
    locationName: formValue.locationName,
    locationCode: formValue.locationCode,
    locationPhone: formValue.phoneNumber,
    locationType: formValue.locationType,
    locationDetail: formValue.locationDetails
  };

  this.manageResidentsService.addNewLocation(payload).pipe(takeUntil(this.destroy$)).subscribe({
    next: (res: any) => {
      if (res.valid) {
        this.snackBar.open(res.message, 'OK', {
          duration: 3000,
        });

        const areaName = this.data.areas.find(a => a.code === formValue.newArea)?.value || '';
        const locationId = res.data;
        const newAssignedLocation: AssignedLocation = {
          areaName: areaName,
          quarterNumber: formValue.qtrNumber,
          blockNumber: formValue.blockNumber,
          locationName: formValue.locationName || 'N/A',
          locationAssignedTo: '',
          id: locationId,
          allocationId: null,
          locationId: res.data || null,
          status: 'Newly Added'
        };

        if (this.data.selectedResidentType === 'IND') {
          // For individual residents, mark all other locations as 'Deleted'
          this.assignedLocationsMap.forEach((loc, key) => {
            if (loc.status !== 'Deleted') {
              this.assignedLocationsMap.set(key, { ...loc, status: 'Deleted' });
            }
          });
        }

        this.assignedLocationsMap.set(locationId, newAssignedLocation);
        this.updateAssignedLocationsDataSource();
        this.onAssign();
        this.resetNewLocationForm();

        const selectedArea = this.locationSearchForm.get('area')?.value;
        if (selectedArea === formValue.newArea) {
          this.refreshLocationsList(selectedArea);
        }
      } else if (res.valid === false) {
        this.snackBar.open(res.message, 'OK', {
          duration: 3000,
        });
      }
      this.cdr.detectChanges();
      setTimeout(() => this.cdr.detectChanges(), 0);
    },
    error: (err: unknown) => {
      console.error('Error saving location:', err);
      const errorMessage = (err as any)?.error?.message || (err as any)?.message || 'Something went wrong. Please try again later.';
      this.snackBar.open(errorMessage, 'OK', {
        duration: 3000,
      });
      this.cdr.detectChanges();
    }
  });
}
resetNewLocationForm(): void {
  // Reset each control individually
  Object.keys(this.newLocationForm.controls).forEach(key => {
    const control = this.newLocationForm.get(key);
    control?.reset(null, { emitEvent: false }); // Explicitly reset to null
    control?.markAsPristine();
    control?.markAsUntouched();
    control?.updateValueAndValidity({ emitEvent: false });
  });

  // Reset the form group
  this.newLocationForm.reset(null, { emitEvent: false });
  this.newLocationForm.markAsPristine();
  this.newLocationForm.markAsUntouched();
  this.newLocationForm.updateValueAndValidity({ emitEvent: false });

  // Debug form state
  console.log('Form state after reset:', {
    value: this.newLocationForm.value,
    touched: this.newLocationForm.touched,
    pristine: this.newLocationForm.pristine,
    valid: this.newLocationForm.valid,
    controls: Object.keys(this.newLocationForm.controls).map(key => ({
      key,
      touched: this.newLocationForm.get(key)?.touched,
      pristine: this.newLocationForm.get(key)?.pristine,
      valid: this.newLocationForm.get(key)?.valid
    }))
  });

  // Force Angular Material to update by triggering change detection twice
  this.cdr.detectChanges();
  setTimeout(() => this.cdr.detectChanges(), 0); // Ensure Material components catch up
}

  refreshLocationsList(areaCode: string): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    
    this.userManagementService.getUserAllocationsByAreaId(Number(areaCode)).pipe(takeUntil(this.destroy$)).subscribe({
      next: (locations: LocationItem[] | any[]) => {
        const formattedLocations = (locations || []).map((loc: any) => ({
          ...loc,
          areaName: this.data.areas.find(a => a.code === areaCode)?.value || ''
        } as LocationItem));

        this.locationsDetails = formattedLocations;
        this.locations.data = this.locationsDetails;

        if (this.availablePaginatorRef) {
          this.locations.paginator = this.availablePaginatorRef;
          this.availablePaginatorRef.length = this.locationsDetails.length;
        }
        this.sortLocations();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error fetching locations:', error);
        this.isLoading = false;
        this.locations.data = [];
        if (this.availablePaginatorRef) {
          this.availablePaginatorRef.length = 0;
        }
        this.cdr.markForCheck();
      }
    });
  }

  onAreaChange(event: MatSelectChange): void {
    this.locations.data = [];
    this.locationSearchForm.get('searchText')?.setValue('');
    
    if (event.value) {
      this.isLoading = true;
      this.cdr.markForCheck();
      
      this.userManagementService.getUserAllocationsByAreaId(event.value).pipe(takeUntil(this.destroy$)).subscribe({
        next: (locations: LocationItem[] | any[]) => {
          const formattedLocations = (locations || []).map((loc: any) => ({
              ...loc,
              areaName: this.data.areas.find(a => a.code === event.value)?.value || ''
            } as LocationItem));

            this.locationsDetails = formattedLocations;
            this.locations.data = this.locationsDetails;
            
            if (this.availablePaginatorRef) {
              this.locations.paginator = this.availablePaginatorRef;
              this.availablePaginatorRef.length = this.locationsDetails.length;
            }
            this.sortLocations();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          console.error('Error fetching locations:', error);
          this.isLoading = false;
          this.locations.data = [];
          if (this.availablePaginatorRef) {
            this.availablePaginatorRef.length = 0;
          }
          this.cdr.markForCheck();
        }
      });
    } else {
      this.locations.data = [];
      if (this.availablePaginatorRef) {
        this.availablePaginatorRef.length = 0;
      }
      this.cdr.markForCheck();
    }
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.locations.filter = filterValue.trim().toLowerCase();

    // After filtering, update the paginator length to the new filtered size
    if (this.locations.paginator) {
      this.locations.paginator.length = this.locations.filteredData.length;
      this.locations.paginator.firstPage();
    }
  }

  resetLocationSearchForm(): void {
    this.locationSearchForm.reset({
      area: '',
      searchText: ''
    });
    this.locations.data = [];
    if (this.availablePaginatorRef) {
      this.availablePaginatorRef.length = 0;
    }
  }

  private getLocationKey(element: LocationItem): string {
    // Try multiple id-like fields which may be populated depending on the API
    if (element.id !== undefined && element.id !== null) return String(element.id);
    if ((element as any).allocationId !== undefined && (element as any).allocationId !== null) return String((element as any).allocationId);
    if (element.locationId !== undefined && element.locationId !== null) return String(element.locationId);
    return `${element.locationName}-${element.qtrNumber || element.quarterNumber}`;
  }

  toggleSelection(element: LocationItem, checked: boolean): void {
    console.log('enter');
    
    const locationId = this.getLocationKey(element);
    const newEntry: AssignedLocation = {
      areaName: element.areaName,
      quarterNumber: (element.qtrNumber || element.quarterNumber) as any,
      blockNumber: element.blockNumber,
      locationName: element.locationName,
      locationAssignedTo: element.residentFirstName
        ? `${element.residentFirstName} ${element.residentLastName}`.trim()
        : 'N/A',
      id: locationId,
      allocationId: element.allocationId || null,
      locationId: element.locationId || null,
      status: 'Newly Added'
    };

    if (this.data.selectedResidentType === 'IND') {
      // For individual residents, only one location can be assigned.
      // Clear any existing non-deleted selections.
      this.assignedLocationsMap.forEach((loc, key) => {
        if (loc.status !== 'Deleted') {
          this.assignedLocationsMap.set(key, { ...loc, status: 'Deleted' });
        }
      });

      if (checked) {
        this.assignedLocationsMap.set(locationId, newEntry);
      }
    } else {
      console.log('wertyui');
      
      // For group residents, allow multiple selections.
      if (checked) {
        this.assignedLocationsMap.set(locationId, newEntry);
      } else {
        const existing = this.assignedLocationsMap.get(locationId);
        if (existing) {
          this.assignedLocationsMap.set(locationId, { ...existing, status: 'Deleted' });
        }
      }
    }
    this.updateAssignedLocationsDataSource();
  }

  isSelected(element: LocationItem): boolean {
    const locationId = this.getLocationKey(element);
    if (!locationId) return false;

    const assignedLocation = this.assignedLocationsMap.get(locationId);
    return !!assignedLocation && assignedLocation.status !== 'Deleted';
  }

  removeLocation(id: string): void {
    if (this.assignedLocationsMap.has(id)) {
      const location = this.assignedLocationsMap.get(id)!;
      location.status = 'Deleted';
      this.updateAssignedLocationsDataSource();
    }
  }

  getSelectedLocationsCount(): number {
    // Deduplicate entries because assignedLocationsMap may contain multiple keys for the same logical
    // AssignedLocation (we seed id/allocationId/locationId). Count unique canonical ids only.
    const uniqueMap = new Map<string, AssignedLocation>();
    for (const loc of this.assignedLocationsMap.values()) {
      const canonicalId = (loc as any).allocationId ?? loc.id ?? (loc as any).locationId ?? `${loc.locationName}-${loc.quarterNumber}`;
      if (!uniqueMap.has(String(canonicalId))) {
        uniqueMap.set(String(canonicalId), loc);
      }
    }
    return Array.from(uniqueMap.values()).filter(location => location.status !== 'Deleted').length;
  }

  sortLocations(): void {
    this.locations.data = this.locations.data.sort((a, b) => {
      const aBlockRaw = a.blockNumber !== undefined && a.blockNumber !== null ? String(a.blockNumber).trim() : '';
      const bBlockRaw = b.blockNumber !== undefined && b.blockNumber !== null ? String(b.blockNumber).trim() : '';

      const aNum = Number(aBlockRaw);
      const bNum = Number(bBlockRaw);

      const aIsNum = aBlockRaw !== '' && !isNaN(aNum);
      const bIsNum = bBlockRaw !== '' && !isNaN(bNum);

      if (aIsNum && bIsNum) {
        return aNum - bNum;
      }

      if (aIsNum && !bIsNum) return -1;
      if (!aIsNum && bIsNum) return 1;

      return aBlockRaw.localeCompare(bBlockRaw);
    });
  }

  onAssign(): void {
    // Deduplicate entries by canonical id before returning to caller.
    const uniqueMap = new Map<string, AssignedLocation>();
    for (const loc of this.assignedLocationsMap.values()) {
      const canonicalId = (loc as any).allocationId ?? loc.id ?? (loc as any).locationId ?? `${loc.locationName}-${loc.quarterNumber}`;
      if (!uniqueMap.has(String(canonicalId))) {
        uniqueMap.set(String(canonicalId), loc);
      }
    }

    const result: FindLocationDialogResult = {
      assignedLocations: Array.from(uniqueMap.values())
    };

    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}